import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Turtle, Trophy, Medal, Crown, TrendingUp, Target, Gamepad2, Award, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  nickname: string;
  avatar?: string;
  points: number;
  totalGames: number;
  correctCount: number;
  winRate: number;
}

type SortType = 'points' | 'winRate' | 'totalGames' | 'correctCount';

const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortType>('points');
  const [myRank, setMyRank] = useState<number | null>(null);
  const [myPoints, setMyPoints] = useState(0);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/user/leaderboard', { params: { type: sortBy, limit: 30 } });
      if (res.data.success && res.data.data) {
        setEntries(res.data.data.leaderboard || []);
        setMyRank(res.data.data.myRank ?? null);
        setMyPoints(res.data.data.myPoints || 0);
      }
    } catch (err) {
      console.error('加载排行榜失败:', err);
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1: return <Crown className="w-6 h-6 text-yellow-500" />;
      case 2: return <Medal className="w-6 h-6 text-gray-400" />;
      case 3: return <Medal className="w-6 h-6 text-amber-600" />;
      default: return <span className={`text-lg font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{rank}</span>;
    }
  };

  const getRankBg = (rank: number) => {
    switch (rank) {
      case 1: return isDark ? 'bg-yellow-900/20 border-yellow-700' : 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300';
      case 2: return isDark ? 'bg-gray-700/40 border-gray-500' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300';
      case 3: return isDark ? 'bg-amber-900/20 border-amber-700' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300';
      default: return isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    }
  };

  const getDisplayValue = (entry: LeaderboardEntry) => {
    switch (sortBy) {
      case 'points': return entry.points;
      case 'winRate': return `${Math.round(entry.winRate)}%`;
      case 'totalGames': return entry.totalGames;
      case 'correctCount': return entry.correctCount;
    }
  };

  const sortOptions: { key: SortType; label: string; icon: React.ReactNode }[] = [
    { key: 'points', label: '积分排行', icon: <Trophy className="w-4 h-4" /> },
    { key: 'winRate', label: '胜率排行', icon: <TrendingUp className="w-4 h-4" /> },
    { key: 'totalGames', label: '游戏次数', icon: <Gamepad2 className="w-4 h-4" /> },
    { key: 'correctCount', label: '破解数', icon: <Target className="w-4 h-4" /> },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-turtle-50 to-white'}`}>
      {/* 导航栏 */}
      <nav className={`sticky top-0 z-50 border-b backdrop-blur-md transition-colors duration-300 ${isDark ? 'bg-gray-800/80 border-gray-700' : 'bg-white/70 border-gray-200'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center space-x-2">
              <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              <Turtle className={`w-6 h-6 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
              <span className={`text-lg font-bold ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>法底捞针</span>
            </button>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>排行榜</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <Trophy className="inline w-8 h-8 mr-2 text-amber-500" />
            排行榜
          </h1>
          <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>看看谁是反诈推理之王</p>
        </motion.div>

        {/* 我的排名 */}
        {myRank && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-xl border-2 ${isDark ? 'bg-ocean-900/30 border-ocean-700' : 'bg-ocean-50 border-ocean-300'}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Award className={`w-6 h-6 ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`} />
                <span className={`font-medium ${isDark ? 'text-ocean-300' : 'text-ocean-700'}`}>
                  我的排名：第 {myRank} 名
                </span>
              </div>
              <span className={`text-sm ${isDark ? 'text-ocean-400' : 'text-ocean-600'}`}>
                {myPoints} 积分
              </span>
            </div>
          </motion.div>
        )}

        {/* 排序选项 */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {sortOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => setSortBy(option.key)}
              className={`flex items-center space-x-1 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                sortBy === option.key
                  ? isDark
                    ? 'bg-ocean-900/50 text-ocean-300 border border-ocean-700'
                    : 'bg-ocean-100 text-ocean-700 border border-ocean-300'
                  : isDark
                    ? 'bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {option.icon}
              <span>{option.label}</span>
            </button>
          ))}
        </div>

        {/* 排行榜列表 */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-ocean-600" />
          </div>
        ) : entries.length === 0 ? (
          <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>暂无排行数据，快去玩几局吧！</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry, index) => (
              <motion.div
                key={entry.userId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`flex items-center p-4 rounded-xl border-2 transition-all hover:shadow-md ${getRankBg(entry.rank)}`}
              >
                <div className="w-10 flex justify-center mr-4">
                  {getRankIcon(entry.rank)}
                </div>
                <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0">
                  {entry.avatar ? (
                    <img src={entry.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <span className={`text-lg font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {(entry.nickname || entry.username || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {entry.nickname || entry.username}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {entry.totalGames} 场游戏 · 破解 {entry.correctCount} 题
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className={`text-xl font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                    {getDisplayValue(entry)}
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {sortBy === 'points' && '积分'}
                    {sortBy === 'winRate' && '胜率'}
                    {sortBy === 'totalGames' && '场次'}
                    {sortBy === 'correctCount' && '破解'}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
